import { defaultCapabilities, defaultLifetime, generateKeyPackage, } from 'ts-mls';
import { b64, suite, utf8 } from './suite.js';
/**
 * Geräte-Identität für MLS.
 *
 * Jedes Gerät ist ein eigenes Gruppenmitglied mit eigenem Schlüsselpaar — kein
 * geteilter Kontoschlüssel (SECURITY.md §3.3). Das ist die Voraussetzung dafür,
 * dass „Gerät sperren" ein Remove-Commit sein kann und nicht nur eine
 * Server-Regel.
 *
 * Der private Teil verlässt das Gerät nie. Er wird ausschließlich im
 * verschlüsselten Schlüsselspeicher abgelegt (`keystore.ts`).
 */
/**
 * Die Identität, die im Credential steht.
 *
 * Bewusst `<personId>:<deviceId>` und nicht der Anzeigename: IDs ändern sich
 * nicht, Namen schon. Beim Vergleich der Sicherheitsnummer soll niemand
 * herausfinden müssen, ob „Mama" inzwischen „Mama ❤" heißt.
 */
export function deviceIdentity(personId, deviceId) {
    return `${personId}:${deviceId}`;
}
/**
 * Erzeugt ein KeyPackage. Der Server hält davon einen kleinen Vorrat, weil
 * jedes beim Einladen verbraucht wird — sonst kann ein Gerät nicht in eine
 * neue Gruppe aufgenommen werden, während es offline ist.
 */
export async function neuesKeyPackage(personId, deviceId) {
    const cs = await suite();
    const kp = await generateKeyPackage({ credentialType: 'basic', identity: utf8.encode(deviceIdentity(personId, deviceId)) }, defaultCapabilities(), defaultLifetime, [], cs);
    return { publicPackage: kp.publicPackage, privatePackage: kp.privatePackage };
}
/** Wie viele KeyPackages ein Gerät auf Vorrat hochlädt. */
export const KEY_PACKAGE_VORRAT = 20;
/**
 * Ab wann nachgelegt wird. Läuft der Vorrat leer, kann das Gerät nicht mehr
 * in neue Gruppen aufgenommen werden — das fällt erst auf, wenn jemand es
 * versucht, deshalb der großzügige Puffer.
 */
export const KEY_PACKAGE_NACHLEGEN_AB = 5;
// --- Sicherheitsnummer -------------------------------------------------------
/**
 * Sicherheitsnummer zum Abgleich zwischen zwei Personen (SECURITY.md §3.3).
 *
 * 60 Ziffern in Zwölfergruppen — dasselbe Format, das Signal etabliert hat, weil
 * es sich vorlesen lässt. Berechnet aus den sortierten Signaturschlüsseln beider
 * Seiten: Beide bekommen dieselbe Zahl, egal wer fragt.
 *
 * Der Zweck ist der MitM-Schutz im TOFU-Fenster. Wer die Zahl vergleicht, weiß,
 * dass zwischen den Geräten niemand sitzt.
 */
export async function sicherheitsnummer(eigenerSchluessel, andererSchluessel) {
    const beide = [b64.encode(eigenerSchluessel), b64.encode(andererSchluessel)].sort();
    const eingabe = utf8.encode(`family-hub/safety-number/v1:${beide[0]}:${beide[1]}`);
    // 5200 Runden wie bei Signal: macht das Vorausberechnen kollidierender
    // Schlüssel teuer, ohne dass der Abgleich spürbar dauert.
    let digest = new Uint8Array(await crypto.subtle.digest('SHA-512', eingabe));
    for (let i = 0; i < 5200; i++) {
        digest = new Uint8Array(await crypto.subtle.digest('SHA-512', digest));
    }
    // Je 5 Byte → eine 5-stellige Gruppe, 12 Gruppen = 60 Ziffern.
    const gruppen = [];
    for (let i = 0; i < 12; i++) {
        let wert = 0;
        for (let j = 0; j < 5; j++)
            wert = wert * 256 + (digest[i * 5 + j] ?? 0);
        gruppen.push(String(wert % 100_000).padStart(5, '0'));
    }
    return gruppen.join(' ');
}
/** Kompakt für den QR-Code — dieselbe Zahl, nur ohne Leerzeichen. */
export function alsQrNutzlast(nummer) {
    return nummer.replace(/\s+/g, '');
}
//# sourceMappingURL=device.js.map